import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Radio,
  Crosshair,
  FileText,
  Settings,
  Eye,
  Radar,
  Telescope,
  Bot,
  Recycle,
  ShieldCheck,
  Users,
  Building2,
  Thermometer,
  Download,
} from 'lucide-react';

export type TraderView =
  | 'dashboard'
  | 'live'
  | 'positions'
  | 'trades'
  | 'settings'
  | 'streamrift'
  | 'g3'
  | 'g4'
  | 'g9';

export type ManagerView = 'manager-traders' | 'manager-org-account' | 'exports' | 'recycler' | TraderView;

export type AdminView = 'admin-manage' | 'admin-traders' | 'admin-g1-health' | 'manager-org-account' | 'exports' | 'recycler' | TraderView;

export type AppView = TraderView;

export interface NavItem<V extends string = string> {
  id: V;
  path: string;
  icon: LucideIcon;
  label: string;
  // 'trade' = always-available core trader nav; 'alpha' = the password-gated
  // dev/alpha testing zone (analytics + experimental, tested live on prod);
  // 'manage' = role-gated org/admin.
  section?: 'manage' | 'trade' | 'alpha';
  /** Optional small corner marker on the icon (e.g. 'test'). */
  badge?: string;
}

export const TRADER_NAV_ITEMS: NavItem<TraderView>[] = [
  // Core trader surface — the product, always available.
  { id: 'dashboard', path: '/dashboard', icon: BarChart3, label: 'Value Dashboard', section: 'trade' },
  { id: 'positions', path: '/positions', icon: Crosshair, label: 'Position Tracker', section: 'trade' },
  { id: 'live', path: '/live', icon: Radio, label: 'Live Discovery', section: 'trade' },
  { id: 'trades', path: '/trades', icon: FileText, label: 'My Trades', section: 'trade' },
  { id: 'settings', path: '/settings', icon: Settings, label: 'Settings', section: 'trade' },
  // Alpha / dev-testing zone — password-gated, tested live on prod.
  { id: 'streamrift', path: '/streamrift', icon: Eye, label: 'StreamRift', section: 'alpha' },
  { id: 'g3', path: '/g3', icon: Radar, label: 'G3', section: 'alpha' },
  { id: 'g4', path: '/g4', icon: Telescope, label: 'G4', section: 'alpha' },
  { id: 'g9', path: '/g9', icon: Bot, label: 'G9 — Auto Assist (testing)', section: 'alpha', badge: 'test' },
];

// Items rendered in the footer (below the user name), in the listed order.
export const FOOTER_NAV_ORDER: readonly string[] = ['settings', 'admin-g1-health', 'manager-traders', 'admin-traders'];
export const FOOTER_NAV_IDS: ReadonlySet<string> = new Set(FOOTER_NAV_ORDER);

export const MANAGER_NAV_ITEMS: NavItem<ManagerView>[] = [
  { id: 'manager-traders', path: '/manager-traders', icon: Users, label: 'Org Settings', section: 'manage' },
  { id: 'manager-org-account', path: '/manager-org-account', icon: Building2, label: 'Org Account', section: 'manage' },
  { id: 'exports', path: '/exports', icon: Download, label: 'Exports', section: 'manage' },
  ...TRADER_NAV_ITEMS,
  // Recycler (G10 edge control) sits at the end of the alpha/lab cluster,
  // completing the G3 → G4 → G9 → G10 progression instead of floating atop the nav.
  { id: 'recycler', path: '/recycler', icon: Recycle, label: 'Recycler', section: 'alpha', badge: 'g10' },
];

export const ADMIN_NAV_ITEMS: NavItem<AdminView>[] = [
  { id: 'admin-manage', path: '/admin-manage', icon: ShieldCheck, label: 'Management', section: 'manage' },
  { id: 'admin-traders', path: '/admin-traders', icon: Users, label: 'Org Settings', section: 'manage' },
  { id: 'exports', path: '/exports', icon: Download, label: 'Exports', section: 'manage' },
  // Admins act as their attached org's manager, so they get the manager
  // Org-Account failsafe view too (own org by default; switch orgs via the
  // Management table). Nothing a manager can do that an admin can't.
  { id: 'manager-org-account', path: '/manager-org-account', icon: Building2, label: 'Org Account', section: 'manage' },
  { id: 'admin-g1-health', path: '/admin-g1-health', icon: Thermometer, label: 'G1 Health', section: 'manage' },
  ...TRADER_NAV_ITEMS,
  // Recycler (G10 edge control) — end of the alpha/lab cluster (G3 → G4 → G9 → G10).
  { id: 'recycler', path: '/recycler', icon: Recycle, label: 'Recycler', section: 'alpha', badge: 'g10' },
];

export const NAV_ITEMS = TRADER_NAV_ITEMS;
